import { AUDIT_BASE_URL } from "../../utils/ApiUrls";

const AUDIT_PROGRAM_BASE_URL = `${AUDIT_BASE_URL}/programs`

class AuditProgramService {
  /**
   * Create a new audit program
   * @param {Object} programData - The audit program data
   * @param {string} accessToken - JWT access token
   */
  async createAuditProgram(programData, accessToken) {
    try {
      // Transform frontend data to backend format
      const backendData = {
        title: programData.title,
        objective: programData.objective,
        reference: programData.reference,
        scope: programData.scope, // Array of strings
        approvers: programData.approvers, // Already array of user IDs
        parties: programData.parties.map(p => ({
          id: p.id,
          partyRole: p.partyRole
        }))
      };

      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(backendData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit program creation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Fetch all audit programs for the organization
   * @param {string} accessToken - JWT access token
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getAuditPrograms(accessToken, filters = {}, paginationData = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_PROGRAM_BASE_URL}/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit programs',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get detailed information about a specific audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async getAuditProgramDetail(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit program details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Update an existing audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   * @param {Object} programData - The updated audit program data
   */
  async updateAuditProgram(accessToken, programId, programData) {
    try {
      // Transform frontend data to backend format
      const backendData = {
        title: programData.title,
        objective: programData.objective,
        reference: programData.reference,
        scope: programData.scope, // Array of strings
        approvers: programData.approvers, // Already array of user IDs
        parties: programData.parties.map(p => ({
          id: p.id,
          partyRole: p.partyRole
        }))
      };

      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/edit/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(backendData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit program update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Delete an audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async deleteAuditProgram(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit program deletion failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Search audit programs for autocomplete/dropdown
   * @param {string} accessToken - JWT access token
   * @param {string} searchTerm - Search query
   */
  async searchAuditPrograms(accessToken, searchTerm = '') {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/search/?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error searching audit programs',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get parties from a specific audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async getProgramParties(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/parties/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching program parties',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get statistics for a specific audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async getProgramStatistics(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/statistics/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching program statistics',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get audit plans for a specific audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getProgramAuditPlans(accessToken, programId, filters = {}, paginationData = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_PROGRAM_BASE_URL}/${programId}/audit-plans/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching program audit plans',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // === AUDIT PROGRAM WORKFLOW METHODS ===

  /**
   * Close an audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async closeAuditProgram(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/close/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error closing audit program',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Reopen a closed audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async reopenAuditProgram(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/reopen/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error reopening audit program',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Mark an audit program as achieved
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async achieveAuditProgram(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/achieve/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error achieving audit program',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Remove achieved status from an audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - The audit program ID
   */
  async unachieveAuditProgram(accessToken, programId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/unachieve/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error unachieving audit program',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Add a party to an audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - Audit program ID
   * @param {number} userId - User ID to add as party
   * @param {string} role - Party role (auditor, auditee, participant, approver)
   */
  async addPartyToAuditProgram(accessToken, programId, userId, role) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/add-party/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          user_id: userId,
          role: role
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error adding party to audit program',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Remove a party from an audit program
   * @param {string} accessToken - JWT access token
   * @param {number} programId - Audit program ID
   * @param {number} partyId - Party ID to remove
   */
  async removePartyFromAuditProgram(accessToken, programId, partyId) {
    try {
      const response = await fetch(`${AUDIT_PROGRAM_BASE_URL}/${programId}/remove-party/${partyId}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error removing party from audit program',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }
}

const auditProgramService = new AuditProgramService();
export default auditProgramService;

 